import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Loader2, CheckCircle2, AlertTriangle, CalendarRange, Clock, RefreshCw } from "lucide-react";
import { timeAgo } from "@/lib/helpers";

function Section({ icon: Icon, title, count, accent, children }) {
  return (
    <div className="border border-zinc-200 bg-white" data-testid={`overview-section-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between border-b border-zinc-200 px-4 py-3">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4" style={{ color: accent }} />
          <h3 className="font-heading text-sm font-semibold uppercase tracking-wider text-zinc-700">{title}</h3>
        </div>
        <span className="border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-xs font-bold text-zinc-600">{count}</span>
      </div>
      <div className="divide-y divide-zinc-100">{children}</div>
    </div>
  );
}

export default function Overview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/reports/weekly-overview"); setData(data); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading || !data) return <div className="flex h-full items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#e84e34]" /></div>;

  const empty = (t) => <p className="px-4 py-3 text-sm text-zinc-400">{t}</p>;

  return (
    <div className="p-6" data-testid="overview-page">
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-zinc-900">End of Week Overview</h2>
          <p className="text-sm text-zinc-400">Task activity across the team · generated {timeAgo(data.generated_at)}</p>
        </div>
        <button onClick={load} data-testid="overview-refresh" className="flex items-center gap-2 border border-zinc-200 px-3 py-2 text-sm font-medium text-zinc-700 hover:border-[#e84e34] hover:text-[#e84e34]"><RefreshCw className="h-4 w-4" /> Refresh</button>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <Section icon={CheckCircle2} title="Completed" count={data.completed.length} accent="#16a34a">
          {data.completed.length === 0 ? empty("No tasks completed this week") : data.completed.map((c, i) => (
            <div key={i} className="flex items-center justify-between px-4 py-2.5">
              <span className="text-sm text-zinc-800">{c.title}</span>
              <span className="text-xs text-zinc-500">by {c.by}</span>
            </div>
          ))}
        </Section>

        <Section icon={AlertTriangle} title="Overdue" count={data.overdue.length} accent="#e84e34">
          {data.overdue.length === 0 ? empty("Nothing overdue 🎉") : data.overdue.map((c, i) => (
            <div key={i} className="flex items-center justify-between px-4 py-2.5">
              <div><span className="text-sm text-zinc-800">{c.title}</span><span className="ml-2 text-xs text-zinc-500">{c.owner}</span></div>
              <span className="text-xs font-semibold text-[#e84e34]">{c.days}d overdue</span>
            </div>
          ))}
        </Section>

        <Section icon={CalendarRange} title="Due Date Changed" count={data.due_changed.length} accent="#ca8a04">
          {data.due_changed.length === 0 ? empty("No due dates changed this week") : data.due_changed.map((c, i) => (
            <div key={i} className="px-4 py-2.5">
              <div className="flex items-center justify-between"><span className="text-sm text-zinc-800">{c.title}</span><span className="text-xs text-zinc-500">{c.owner}</span></div>
              <p className="mt-0.5 text-xs text-zinc-400">{c.detail}</p>
            </div>
          ))}
        </Section>

        <Section icon={Clock} title="Due Next Week" count={data.due_next_week.length} accent="#2563eb">
          {data.due_next_week.length === 0 ? empty("Nothing due next week") : data.due_next_week.map((c, i) => (
            <div key={i} className="flex items-center justify-between px-4 py-2.5">
              <div><span className="text-sm text-zinc-800">{c.title}</span><span className="ml-2 text-xs text-zinc-500">{c.owner}</span></div>
              <span className="text-xs text-zinc-500">{c.due}</span>
            </div>
          ))}
        </Section>
      </div>
    </div>
  );
}
